// Centralised case study data with full content for /case-studies and /case-studies/:slug pages.

export const CASE_STUDIES_FULL = [
  {
    slug: "uae-fintech-rank-2",
    category: "FinTech",
    appName: "PaySprint",
    title: "From rank #38 to #2 on Google Play",
    metric: "+412%",
    metricLabel: "organic installs in 90 days",
    summary:
      "Re-engineered keyword stack, rebuilt screenshots, and shipped review reply playbook for a UAE neo-bank targeting expats.",
    market: "UAE · Google Play",
    platform: "android",
    services: ["ASO", "Ratings & Reviews"],
    duration: "90 days",
    challenge:
      "PaySprint had product-market fit but was invisible in Google Play UAE search. They ranked #38 for 'mobile banking' and below #100 for every Arabic equivalent. Rating was stuck at 3.9 with negative reviews compounding.",
    approach: [
      "Built a bilingual keyword stack — Arabic primary, English secondary — covering 38 long-tail finance queries.",
      "Rewrote title, short description and full description across both languages.",
      "Designed 5 new screenshot sets featuring benefit-led headlines, native faces and AED-denominated examples.",
      "Stood up review reply ops covering every 1–3★ review within 24 hours.",
      "Implemented smart in-app rating prompts triggered at the first successful transaction.",
    ],
    results: [
      { value: "#2", label: "rank for 'mobile banking' (was #38)" },
      { value: "+412%", label: "organic installs in 90 days" },
      { value: "4.5★", label: "average rating (was 3.9★)" },
      { value: "−63%", label: "drop in negative reviews" },
    ],
    quote: {
      text: "Lumotis cracked the UAE App Store for us. Bilingual keyword work and review ops took us from invisible to default option in 90 days.",
      author: "Omar Al-Farsi · Founder, PaySprint",
    },
  },
  {
    slug: "usa-gaming-top-10",
    category: "Gaming",
    appName: "LoopQuest",
    title: "Top 10 Casual chart, USA App Store",
    metric: "+267%",
    metricLabel: "lift in conversion rate",
    summary:
      "ASO + Apple Search Ads combo brought a Jaipur-built casual game to the US Top 10 Casual chart within four months.",
    market: "USA · App Store",
    platform: "ios",
    services: ["ASO", "User Acquisition"],
    duration: "4 months",
    challenge:
      "LoopQuest had strong retention but couldn't break out of #80–#120 in the US App Store Casual chart. Apple Search Ads spending wasn't translating into chart momentum because organic CVR was sub-15%.",
    approach: [
      "Reframed the listing around the gameplay loop, not the genre — three crisp benefit screenshots.",
      "Shipped iOS Custom Product Pages: brand-keyword variant, competitor-keyword variant, default.",
      "Reworked Apple Search Ads into a Discovery / Exact / Competitor 3-account structure.",
      "Synced organic ASO and ASA so paid-organic ratios stayed healthy as we scaled spend.",
    ],
    results: [
      { value: "Top 10", label: "USA App Store · Casual category" },
      { value: "+267%", label: "App Store conversion rate" },
      { value: "−38%", label: "Apple Search Ads CPI" },
      { value: "4×", label: "organic install velocity" },
    ],
    quote: {
      text: "Their organic ASO did half the job before we even spent on Apple Search Ads. Best US-market work we've seen.",
      author: "Karthik Rao · Founder, LoopQuest Studios",
    },
  },
  {
    slug: "health-4-8-stars",
    category: "Health",
    appName: "CalmRoot",
    title: "4.2 → 4.8 star rating in 60 days",
    metric: "−63%",
    metricLabel: "drop in negative reviews",
    summary:
      "Smart in-app prompts, onboarding rework and reply ops lifted ratings for a global meditation app on iOS + Android.",
    market: "Global · iOS + Android",
    platform: "both",
    services: ["Ratings & Reviews", "App Retention"],
    duration: "60 days",
    challenge:
      "CalmRoot was getting good downloads but their 4.2 average rating capped CVR. The team had no rating prompt logic and was replying to about 18% of negative reviews.",
    approach: [
      "Replaced the launch-time rating prompt with a contextual one — fired after first completed meditation.",
      "Added sentiment pre-filter routing unhappy users to a private feedback form.",
      "Stood up reply ops covering 100% of 1–3★ reviews within 24h.",
      "Mined review themes back into the product roadmap and the listing copy.",
    ],
    results: [
      { value: "4.8★", label: "average rating (was 4.2★)" },
      { value: "−63%", label: "drop in negative reviews" },
      { value: "100%", label: "reply rate on 1–3★ reviews" },
      { value: "+32%", label: "App Store CVR" },
    ],
    quote: {
      text: "Lumotis moved our app from 4.2 to 4.8 in 60 days. It's the most underrated growth lever we've ever pulled.",
      author: "Amelia Carter · Head of Growth, CalmRoot",
    },
  },
  {
    slug: "edtech-india-top-3",
    category: "EdTech",
    appName: "EduPond Kids",
    title: "Top 3 in Education (India), Google Play",
    metric: "+189%",
    metricLabel: "organic installs in 120 days",
    summary:
      "Full listing rewrite in English + Hindi, 12 localized screenshot sets and Apple Search Ads rollout to Tier-1 metros.",
    market: "India · Google Play",
    platform: "android",
    services: ["ASO", "User Acquisition"],
    duration: "120 days",
    challenge:
      "EduPond Kids dominated Tier-1 metros via word-of-mouth but had zero ASO — they were invisible in Hindi-language search and losing 70% of their TAM.",
    approach: [
      "Built parallel English + Hindi keyword stacks with locale-specific synonyms.",
      "Shipped 12 localized screenshot variants across 4 grade-bands.",
      "Rolled out Apple Search Ads in 7 Indian Tier-1 metros with city-specific creative.",
      "Implemented review reply ops in both Hindi and English.",
    ],
    results: [
      { value: "Top 3", label: "India · Google Play · Education" },
      { value: "+189%", label: "organic installs in 120 days" },
      { value: "+71%", label: "Hindi-keyword install share" },
      { value: "4.7★", label: "average rating" },
    ],
    quote: {
      text: "Lumotis understood the Indian app store better than we did — and we're an Indian team. Bilingual ASO unlocked the rest of India for us.",
      author: "Anushka Verma · Co-founder, EduPond Kids",
    },
  },
  {
    slug: "food-delivery-rank-3",
    category: "Food Delivery",
    appName: "MealFox",
    title: "From #29 to #3 for 'food delivery'",
    metric: "+340%",
    metricLabel: "keyword-driven installs",
    summary:
      "Comprehensive keyword refactor and reply-ops campaign took a regional food-delivery app to top-3 for its money keyword.",
    market: "India · iOS + Android",
    platform: "both",
    services: ["ASO", "Ratings & Reviews"],
    duration: "100 days",
    challenge:
      "MealFox was buried at #29 for the highest-volume keyword in their category. Reviews were piling up unanswered. They were losing share to better-marketed but worse-product competitors.",
    approach: [
      "Refactored the keyword universe around 'food delivery' as the seed plus 12 long-tail clusters.",
      "Rebuilt the screenshot story to lead with delivery-time guarantees — the actual product moat.",
      "Stood up reply ops + smart prompts; rating climbed from 4.0 to 4.6.",
      "Cross-promoted across iOS and Google Play with platform-specific creative.",
    ],
    results: [
      { value: "#3", label: "Google Play India for 'food delivery'" },
      { value: "+340%", label: "keyword-driven installs" },
      { value: "4.6★", label: "average rating (was 4.0★)" },
      { value: "+78%", label: "App Store CVR" },
    ],
    quote: {
      text: "Lumotis moved us from rank #29 to #3 on Google Play in three months. Their ASO and screenshot strategy is unreal.",
      author: "Priya Iyer · Founder, MealFox",
    },
  },
  {
    slug: "meditation-ai-citation",
    category: "Wellness",
    appName: "StillHour",
    title: "AI-cited inside ChatGPT + Perplexity",
    metric: "11×",
    metricLabel: "more LLM citations / 100 prompts",
    summary:
      "GEO engagement got a global meditation app cited across ChatGPT, Claude and Perplexity — a new high-intent channel.",
    market: "Global · LLMs",
    platform: "both",
    services: ["GEO / AI Citation"],
    duration: "90 days",
    challenge:
      "StillHour had strong organic but no AI presence. When we baseline-scanned ChatGPT, Claude, Gemini and Perplexity, they appeared in 6 / 100 category prompts. Their main competitor appeared in 47.",
    approach: [
      "Mapped 200 high-intent meditation queries users were asking LLMs.",
      "Engineered citation-worthy assets: comparison pages, research summaries, founder Q&A.",
      "Seeded knowledge-graph entities (Wikidata, G2, Product Hunt, Crunchbase).",
      "Stood up LLM answer monitoring with weekly share-of-voice reports.",
    ],
    results: [
      { value: "11×", label: "more LLM citations / 100 prompts" },
      { value: "47%", label: "share of voice in meditation prompts" },
      { value: "5", label: "models actively citing (ChatGPT, Claude, Gemini, Perplexity, Copilot)" },
      { value: "+18%", label: "branded App Store searches downstream" },
    ],
    quote: {
      text: "Lumotis's GEO work alone is worth the engagement. We now show up inside ChatGPT and Perplexity answers for our category — that's our biggest new channel of 2026.",
      author: "Mira Hasan · CMO, StillHour",
    },
  },
];

export function getCaseStudyBySlug(slug) {
  return CASE_STUDIES_FULL.find((c) => c.slug === slug);
}
